'use client'

import { type Input } from '@builder.io/sdk'

import { configPublic } from '@src/config.public'

export const ActionNames = [
  'none',
  'navigate',
  'addItemsToCart',
  'upsellToCart',
  'revertUpsell',
  'popoutVideo',
  'openModal',
  'selectSKU',
  'addSelectedSKUsToCart',
  'loginModal',
  'openChatbotWindow',
] as const

export const ANALYTICS_MODULE_INPUT = {
  name: 'analyticsModule',
  friendlyName: 'Analytics Module',
  type: 'string',
  defaultValue: '',
  helperText:
    'Tag that identifies this CTA in Amplitude. (optional, if empty defaults to "unknown")',
}

export type ActionName = (typeof ActionNames)[number]

export const AnalyticsEvents = [
  'none',
  'select cta',
  'select rail cta',
  'select nav cta',
  'select sticky footer cta',
  'select pencil banner cta',
] as const

export const REDIRECT_HELPER_TEXT = 'URL to navigate to after adding to cart (optional)'

const redirectUrl = {
  name: 'redirectUrl',
  friendlyName: 'Redirect URL',
  type: 'text',
  helperText: REDIRECT_HELPER_TEXT,
}

const shopifyRedirectUrl = {
  name: 'shopifyRedirectUrl',
  friendlyName: 'Shopify Redirect URL',
  type: 'text',
  helperText: REDIRECT_HELPER_TEXT.replace('cart', 'Shopify cart'),
}

export const clickActionInputs: Input[] = [
  {
    name: 'actionName',
    friendlyName: 'Click Action',
    type: 'enum',
    defaultValue: 'none',
    enum: ActionNames.map(String),
  },
  {
    ...ANALYTICS_MODULE_INPUT,
    showIf: (options) => options.get('actionName') !== 'none',
  },
  {
    name: 'navigate',
    friendlyName: 'Navigate Options',
    type: 'object',
    showIf: (options) => options.get('actionName') === 'navigate',
    defaultValue: { href: '/', analyticsEvent: 'none' },
    subFields: [
      {
        name: 'href',
        friendlyName: 'URL',
        type: 'text',
      },
      {
        name: 'target',
        friendlyName: 'Tab Behavior',
        helperText: 'Determines if the link opens in the same or new tab. Default is "Same tab".',
        type: 'enum',
        enum: [
          { value: '_self', label: 'Same tab' },
          { value: '_blank', label: 'New tab' },
        ],
        defaultValue: '_self',
      },
      {
        name: 'analyticsEvent',
        friendlyName: 'Analytics Event',
        type: 'enum',
        defaultValue: 'none',
        enum: AnalyticsEvents.map(String),
      },
    ],
  },
  {
    name: 'addItemsToCart',
    friendlyName: 'Add Items to Cart',
    type: 'object',
    showIf: (options) => options.get('actionName') === 'addItemsToCart',
    subFields: [
      {
        name: 'skus',
        friendlyName: 'Items',
        type: 'list',
        subFields: [
          {
            name: 'sku',
            friendlyName: 'SKU',
            type: 'text',
          },
          {
            name: 'variantId',
            friendlyName: 'Shopify Variant ID (QA/Default)',
            type: 'text',
            required: configPublic.featureFlags.shopifyEnabled,
            helperText: 'Used for QA and as fallback for other environments',
          },
          {
            name: 'subscriptionPlanId',
            friendlyName: 'Subscription Plan ID (QA/Default)',
            type: 'text',
            helperText: 'External Plan ID from Recharge - Used for QA and as fallback',
          },
          {
            name: 'variantIdStage',
            friendlyName: 'Shopify Variant ID (Stage)',
            type: 'text',
            helperText: 'Stage environment variant ID',
          },
          {
            name: 'subscriptionPlanIdStage',
            friendlyName: 'Subscription Plan ID (Stage)',
            type: 'text',
            helperText: 'Stage environment subscription plan ID',
          },
          {
            name: 'variantIdProduction',
            friendlyName: 'Shopify Variant ID (Production)',
            type: 'text',
            helperText: 'Production environment variant ID',
          },
          {
            name: 'subscriptionPlanIdProduction',
            friendlyName: 'Subscription Plan ID (Production)',
            type: 'text',
            helperText: 'Production environment subscription plan ID',
          },
          {
            name: 'quantity',
            friendlyName: 'Quantity',
            type: 'number',
            defaultValue: 1,
          },
        ],
      },
      redirectUrl,
      shopifyRedirectUrl,
    ],
  },
  {
    name: 'upsellToCart',
    friendlyName: 'Upsell Options',
    type: 'object',
    showIf: (options) => options.get('actionName') === 'upsellToCart',
    subFields: [
      {
        name: 'skus',
        friendlyName: 'SKUs',
        type: 'list',
        subFields: [
          {
            name: 'sku',
            friendlyName: 'SKU',
            type: 'text',
          },
          {
            name: 'quantity',
            friendlyName: 'Quantity',
            type: 'number',
            defaultValue: 1,
          },
        ],
      },
      redirectUrl,
    ],
  },
  {
    name: 'revertUpsell',
    friendlyName: 'Revert Upsell Options',
    type: 'object',
    showIf: (options) => options.get('actionName') === 'revertUpsell',
    subFields: [redirectUrl],
  },
  {
    name: 'popoutVideo',
    friendlyName: 'Open video player modal',
    type: 'object',
    showIf: (options) => options.get('actionName') === 'popoutVideo',
    subFields: [
      {
        name: 'videoId',
        type: 'text',
        defaultValue: '6359706013112',
      },
    ],
  },
  {
    name: 'openModal',
    friendlyName: 'Open modal with id',
    type: 'object',
    showIf: (options) => options.get('actionName') === 'openModal',
    subFields: [
      {
        name: 'modalId',
        type: 'text',
      },
      {
        name: 'analyticsEvent',
        friendlyName: 'Analytics Event',
        type: 'enum',
        defaultValue: 'none',
        enum: AnalyticsEvents.map(String),
      },
    ],
  },
  {
    name: 'selectSKU',
    friendlyName: 'Select/unselect SKU in a Rail',
    type: 'object',
    showIf: (options) => options.get('actionName') === 'selectSKU',
    subFields: [
      {
        name: 'sku',
        type: 'string',
      },
    ],
  },
  {
    name: 'addSelectedSKUsToCart',
    friendlyName: 'Add selected SKUs to cart',
    type: 'object',
    showIf: (options) => options.get('actionName') === 'addSelectedSKUsToCart',
    subFields: [
      {
        name: 'extraSkus',
        friendlyName: 'Extra SKUs',
        localized: true,
        type: 'array',
        subFields: [
          {
            name: 'sku',
            type: 'string',
          },
        ],
      },
      {
        name: 'bundleId',
        type: 'string',
        localized: true,
      },
      redirectUrl,
    ],
  },
  {
    name: 'loginModal',
    friendlyName: 'Login Modal',
    type: 'object',
    showIf: (options) => options.get('actionName') === 'loginModal',
    subFields: [
      {
        name: 'isPreviewing',
        type: 'boolean',
        defaultValue: false,
      },
      {
        name: 'heading',
        type: 'string',
        defaultValue: 'BODi Members Only!',
        localized: true,
      },
      {
        name: 'subHeading',
        type: 'string',
        localized: true,
        defaultValue:
          'Turn your BODi fitness journey into a rewarding side hustle—or more. For members only!',
      },
      {
        name: 'disclaimer',
        type: 'html',
        localized: true,
        defaultValue: 'By continuing, I agree to the Terms and Conditions and Privacy Policy.',
      },
      {
        name: 'redirectUrl',
        type: 'string',
        defaultValue: '/checkout',
      },
    ],
  },
]
